import * as ko from "knockout";
import {IMessageData, ISyncField} from "../../../index";
import {IAvailableData, TColumnsConfig, TColumnsTranslation} from "./index";
import {Group} from "./group";
import {ILanguage, REQUEST_MESSAGE} from "../../../constants/Language";
import {storeObservable, storeObservableArray} from "../../../util/sessionstore";
import {ImportFormatId} from "../../../constants/ImportFormats";
import {ObjectTypeId, Permission} from "../../../constants";
import {ButtonManager} from "../../../util/ButtonManager";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {Table} from "./table";
import {global} from "../../../appContext";
import {createErrorMessage, getPermissionDefinition} from "../../../util/misc";
import PromiseRequest from "../../../req/PromiseRequest";
import {_} from 'lodash';
import {getDiscardButton, getSubmitButton} from "../../../constants/RouteDefinitions";
import {viewModel} from "../details";
import pubsub from "../../../pubsub";
import {nsSystemfields} from "../nsSystemfields";
import detailsHtml from './details.html?raw';

export const template = detailsHtml;

export class List {
    private readonly isMutable = nsSystemfields.isMutable

    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null)
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null)
    private readonly pageButtons: ButtonManager = new ButtonManager()

    private readonly selectedImportFormatId: KnockoutObservable<ILanguage['id']> = storeObservable('syncfields.config.importformat', ImportFormatId.Visma)
    private readonly selectedLanguageId: KnockoutObservableArray<ILanguage['id']> = storeObservableArray('syncfields.config.language')
    private readonly selectedObjectTypeId: KnockoutObservable<ObjectTypeId> = storeObservable('syncfields.config.objecttypeid')

    private readonly availableData: IAvailableData = {
        columnsConfig: ko.observableArray(),
        columnsTranslation: ko.observableArray(),
        objectTypes: [],
        groups: ko.observableArray(),
        permissionDefinitions: [Permission.Employee, Permission.SuperEmployee, Permission.DepartmentAdmin, Permission.SuperManager, Permission.CompanyAdmin, Permission.OrganizationAdmin, Permission.SuperAdmin].map(level => getPermissionDefinition(level)),
        selectedImportFormatId: this.selectedImportFormatId,
        selectedLanguageId: this.selectedLanguageId,
        selectedObjectTypeId: this.selectedObjectTypeId,
    }

    private req_getsyncfields!: PromiseRequest[]
    private readonly tableOld: Table;
    private readonly tableNew: Table;

    constructor(private viewModel: viewModel) {
        pubsub.on(this, 'copy.systemfield', (event: JQuery.Event, group: Group) => {
            const g = new Group(group.getSubmitData(), this.availableData, this.tableNew)
            g.mode('create')
            group.items.forEach(i => {
                const syncfield = i.getSubmitData();
                g.addSyncfield(syncfield)
            })
            this.availableData.groups.push(g)
            g.init()
        })

        this.availableData.objectTypes = viewModel.objectTypes

        this.tableOld = new Table(this.availableData, {
            modes: ['read', 'update', 'delete'],
            searchkey: storeObservable('syncfields.crud.searchkey'),
            sortcol: 'syncfieldid',
        })
        this.tableNew = new Table(this.availableData, {modes: ['create']})

        this.kosub.subscribeChanged(this.availableData.selectedObjectTypeId, () => this.tableOld.pagenr(0))
        this.kosub.subscribeChanged(this.availableData.selectedObjectTypeId, () => requestAnimationFrame(() => this.preInit()))
        this.kosub.subscribeChanged(this.availableData.selectedLanguageId, () => requestAnimationFrame(() => this.preInit()))
        this.kosub.subscribeChanged(this.availableData.selectedImportFormatId, () => requestAnimationFrame(() => this.preInit()))
        this.kosub.subscribeChanged(this.tableOld.searchkey, () => this.tableOld.pagenr(0))

        this.pageButtons.add(getDiscardButton({
            onClick: this.preInit.bind(this),
            enabled: ko.pureComputed(() => !!this.numChanges()),
            validator: () => nsSystemfields.isMutable,
        }))

        this.pageButtons.add(getSubmitButton({
            onClick: this.onSubmit.bind(this),
            label: () => {
                let ret = '((save))'
                if (this.numChanges()) {
                    ret += ` [${this.numChanges()}]`
                }
                return ret
            },
            enabled: ko.pureComputed(() => !this.tableOld.errorManager.getNumErrors() && !this.tableNew.errorManager.getNumErrors() && !!this.numChanges()),
            validator: () => nsSystemfields.isMutable,
        }))

        this.preInit()
    }

    private preInit() {
        this.isReady(false)
        this.availableData.groups().forEach(g => g.uninit())
        this.availableData.groups.removeAll()
        this.req_getsyncfields?.forEach(o => o.abort())
        this.req_getsyncfields = []
        const a = this.availableData.selectedObjectTypeId()
        const b = this.availableData.selectedLanguageId().length
        const c = this.availableData.selectedImportFormatId()
        if (!!a && !!b && !!c) {
            let columnsConfig: TColumnsConfig[]
            switch (this.availableData.selectedObjectTypeId()) {
                case ObjectTypeId.User:
                case ObjectTypeId.Department:
                case ObjectTypeId.Group:
                case ObjectTypeId.ApiUser: {
                    columnsConfig = ['syncfieldid', 'name', 'required', 'ignorefilter', 'issynced', 'isoverridablebylevel']
                    break
                }
                default: {
                    columnsConfig = ['syncfieldid', 'name']
                    break
                }
            }
            this.availableData.columnsConfig(columnsConfig)
            let columnsTranslation: TColumnsTranslation[]
            switch (this.availableData.selectedObjectTypeId()) {
                case ObjectTypeId.Function:
                case ObjectTypeId.FormFunction: {
                    columnsTranslation = ['syncfielddisplaynameid', 'displayname', 'description', 'core', 'parameters']
                    break
                }
                case ObjectTypeId.BEStandard:
                case ObjectTypeId.FEStandard: {
                    columnsTranslation = ['syncfielddisplaynameid', 'displayname', 'description', 'core']
                    break
                }
                case ObjectTypeId.User:
                case ObjectTypeId.Department:
                case ObjectTypeId.Group:
                case ObjectTypeId.ApiUser: {
                    columnsTranslation = ['syncfielddisplaynameid', 'displayname', 'description', 'core', 'fieldtype']
                    break
                }
                default: {
                    columnsTranslation = ['syncfielddisplaynameid', 'displayname', 'description', 'core']
                    break
                }
            }
            this.availableData.columnsTranslation(columnsTranslation)
            this.init()
        } else {
            this.messageData({message: 'awaiting context'})
        }
    }

    private init() {
        this.isReady(false)
        this.messageData({message: REQUEST_MESSAGE})
        const groups: Group[] = []
        const requests = this.availableData.selectedLanguageId().map(languageId => {
            const promiseRequest = global.getPromiseRequest('getsyncfields')
            this.req_getsyncfields.push(promiseRequest)
            const importFormatId = this.availableData.selectedImportFormatId()
            const objectTypeId = this.availableData.selectedObjectTypeId()
            const params = {
                importformatid: importFormatId,
                languageid: languageId,
                objecttypeid: objectTypeId,
            };
            return promiseRequest.request(params)
                .then((data: ISyncField[]) => {
                    data.forEach(sf => {
                        let group = groups.find(o => o.syncfield.syncfieldid == sf.syncfieldid)
                        if (group) {
                            group.addSyncfield(sf)
                        } else {
                            group = new Group(sf, this.availableData, this.tableOld)
                            group.addSyncfield(sf)
                            groups.push(group)
                        }
                    })
                })
        })
        Promise.all(requests)
            .then(() => {
                this.availableData.groups.push(...groups)
                const message = this.availableData.groups().length
                    ? null
                    : {message: 'no data'}
                this.messageData(message)
                this.isReady(true)
            })
            .catch((reason) => {
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    private onAddNewClick() {
        const syncfield: ISyncField = {
            core: false,
            description: '',
            displayname: '',
            fieldtype: 'String',
            ignorefilter: false,
            importformatid: 0,
            isoverridablebylevel: Permission.CompanyAdmin,
            issynced: false,
            languageid: 0,
            maxlength: 0,
            metaidentifier: '',
            name: '' as any,
            objecttypeid: this.availableData.selectedObjectTypeId(),
            parameters: '',
            referencefieldid: 0,
            referenceobjecttypeid: 0,
            required: false,
            syncfielddisplaynameid: 0,
            syncfieldid: 0,
            value: '',
        };
        const group = new Group(syncfield, this.availableData, this.tableNew)
        group.mode('create')
        this.availableData.selectedLanguageId().forEach(languageid => {
            const sf = _.defaultsDeep({languageid: languageid}, syncfield)
            group.addSyncfield(sf)
        })
        this.availableData.groups.push(group)
        group.init()
    }

    private onSubmit() {
        this.viewModel.submit(this.availableData.groups)
    }

    private numChanges(): number {
        return this.tableOld.numChanges() + this.tableNew.numChanges()
    }

    dispose() {
        pubsub.off(this)
        this.kosub.unsuscribeAll()
        this.req_getsyncfields?.forEach(o => o.abort())
        this.availableData.groups().forEach(g => g.uninit())
        this.availableData.groups.removeAll()
    }

}